import { useEffect, useState } from 'react';
import ErrorBoundary from '../common/ErrorBoundary';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboardIcon,
  BuildingIcon,
  TrendingUpIcon,
  FlaskConicalIcon,
  CreditCardIcon,
  BellIcon,
  LogOutIcon,
  MenuIcon,
  RefreshCwIcon,
  ReceiptIcon,
  PuzzleIcon,
  ShieldCheckIcon,
  SettingsIcon,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { ROUTES } from '../../constants/routes';
import { cn } from '../../utils/cn';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';

const PLATFORM_NAV = [
  { section: 'Overview', items: [
    { label: 'Dashboard', href: ROUTES.PLATFORM, icon: LayoutDashboardIcon, exact: true },
  ]},
  { section: 'Clients', items: [
    { label: 'All Companies', href: ROUTES.PLATFORM_CLIENTS, icon: BuildingIcon },
    { label: 'Subscriptions', href: ROUTES.PLATFORM_SUBSCRIPTIONS, icon: RefreshCwIcon },
    { label: 'Trials', href: ROUTES.PLATFORM_TRIALS, icon: FlaskConicalIcon },
  ]},
  { section: 'Billing', items: [
    { label: 'Billing Overview', href: ROUTES.PLATFORM_BILLING, icon: CreditCardIcon },
    { label: 'Invoices', href: ROUTES.PLATFORM_INVOICES, icon: ReceiptIcon },
    { label: 'Revenue', href: ROUTES.PLATFORM_REVENUE, icon: TrendingUpIcon },
  ]},
  { section: 'Analytics', items: [
    { label: 'Module Adoption', href: ROUTES.PLATFORM_MODULES, icon: PuzzleIcon },
  ]},
  { section: 'System', items: [
    { label: 'Activity & Audit', href: ROUTES.PLATFORM_ACTIVITY, icon: ShieldCheckIcon },
    { label: 'Settings', href: ROUTES.PLATFORM_SETTINGS, icon: SettingsIcon },
  ]},
];

function PlatformSidebar({ onClose }: { onClose?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const user = useAuthStore((s) => s.user);

  function handleNav(href: string) {
    onClose?.();
    navigate(href);
  }

  function handleSignOut() {
    onClose?.();
    clearAuth();
    queryClient.clear();
    navigate(ROUTES.LOGIN, { replace: true });
  }

  return (
    <div className="flex h-full flex-col bg-gray-900 text-white">
      {/* Brand */}
      <div className="flex h-16 items-center px-4 border-b border-white/10">
        <span className="text-base font-bold tracking-tight">Fastclaim</span>
        <span className="ml-2 text-xs text-white/50">Platform</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-4">
        {PLATFORM_NAV.map((section) => (
          <div key={section.section}>
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/30">
              {section.section}
            </p>
            {section.items.map((item) => {
              const isActive = item.exact
                ? pathname === item.href
                : pathname === item.href || pathname.startsWith(item.href + '/');
              return (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => handleNav(item.href)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors mb-1',
                    isActive
                      ? 'bg-white/15 text-white font-medium'
                      : 'text-white/70 hover:bg-white/10 hover:text-white',
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-white/10 p-4 space-y-2">
        {user && (
          <p className="text-xs text-white/50 truncate">{user.email}</p>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10 px-2"
          onClick={handleSignOut}
        >
          <LogOutIcon className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );
}

export default function PlatformLayout() {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const unreadCount = useNotificationStore((s) => s.unread_count);
  const setDrawerOpen = useNotificationStore((s) => s.setDrawerOpen);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Reset branding to Fastclaim defaults on mount
  useEffect(() => {
    const root = document.documentElement;
    root.style.removeProperty('--color-primary');
    root.style.removeProperty('--color-primary-dark');
    root.style.removeProperty('--color-primary-light');
    root.style.removeProperty('--color-secondary');
    root.style.removeProperty('--color-sidebar-bg');
    root.style.removeProperty('--color-sidebar-dark');
    root.style.removeProperty('--color-sidebar-item');
  }, []);

  if (!isSuperAdmin()) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 flex-col flex-shrink-0">
        <PlatformSidebar />
      </aside>

      {/* Mobile nav */}
      <Sheet open={mobileOpen} onOpenChange={(o) => !o && setMobileOpen(false)}>
        <SheetContent side="left" className="p-0 w-60 bg-gray-900">
          <SheetHeader className="sr-only">
            <SheetTitle>Platform Navigation</SheetTitle>
          </SheetHeader>
          <PlatformSidebar onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* TopNav */}
        <header className="flex h-16 shrink-0 items-center justify-between border-b bg-white px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon className="h-5 w-5" />
            </Button>
            <span className="text-sm font-bold text-gray-900">Fastclaim</span>
            <span className="text-xs text-gray-400">Platform Admin</span>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setDrawerOpen(true)}
            aria-label="Notifications"
          >
            <BellIcon className="h-5 w-5" />
            {unreadCount > 0 && (
              <Badge className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full p-0 text-[10px] bg-red-500 text-white border-0">
                {unreadCount}
              </Badge>
            )}
          </Button>
        </header>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
